import { useEffect, useState } from 'react';

import api from 'api/api';

/*
 * Viewing offers in the other currency: an offer of a Swedish department is calculated in
 * kronas and the others in euros. The rate is the reference rate of the European Central
 * Bank at the time of viewing. Only what is shown changes: no saved price is converted.
 */

export type MoneyCurrency = 'EUR' | 'SEK';

export interface IExchangeRate {
  // The day of the rate
  date: string;
  // How many kronas a euro is
  rate: number;
  // e.g. ECB
  source: string;
}

/** The currency of an offer by the country of its department */
export const currencyOfCountry = (country?: null | string): MoneyCurrency =>
  country === 'SE' ? 'SEK' : 'EUR';

/** The sign of the currency after an amount */
export const currencySymbol = (currency: MoneyCurrency): string =>
  currency === 'SEK' ? 'SEK' : '€';

/** The currency of a report: kronas only when all the offers viewed are Swedish */
export const getReportCurrency = (
  viewedCountries: (null | string | undefined)[]
): MoneyCurrency =>
  viewedCountries.length > 0 &&
  viewedCountries.every((country) => currencyOfCountry(country) === 'SEK')
    ? 'SEK'
    : 'EUR';

/** The amount in the other currency; as it is without a rate */
export const convertMoney = (
  value: number,
  from: MoneyCurrency,
  to: MoneyCurrency,
  rate: null | IExchangeRate
): number => {
  if (from === to || rate === null || !(rate.rate > 0)) {
    return value;
  }

  return from === 'EUR' ? value * rate.rate : value / rate.rate;
};

// Fetched once for the page; a failed fetch is tried again by the next view
let loading: null | Promise<null | IExchangeRate> = null;

const getExchangeRate = (): Promise<null | IExchangeRate> => {
  if (loading === null) {
    loading = api
      .get('/api/v1/exchange-rates/eur-sek')
      .then((rate: IExchangeRate | '') => rate || null)
      .catch(() => null)
      .then((rate: null | IExchangeRate) => {
        if (rate === null) {
          loading = null;
        }
        return rate;
      });
  }

  return loading as Promise<null | IExchangeRate>;
};

/** The rate of the euro and the krona, null until it is fetched or when it cannot be */
export const useExchangeRate = (enabled = true): null | IExchangeRate => {
  const [rate, setRate] = useState<null | IExchangeRate>(null);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    let active = true;
    getExchangeRate().then((fetched) => active && setRate(fetched));
    return () => {
      active = false;
    };
  }, [enabled]);

  return rate;
};

/**
 * The currency of a report of offers of several departments: the sums of the Swedish
 * offers (in SEK) and the others (in €) are added in one currency. Euros, unless all the
 * offers viewed are Swedish; the user can switch to the other.
 */
export const useReportCurrency = (
  viewedCountries: (null | string | undefined)[]
): {
  convertFrom: (country: null | string | undefined, value: number) => number;
  currency: MoneyCurrency;
  rate: null | IExchangeRate;
  setCurrency: (currency: MoneyCurrency) => void;
  symbol: string;
} => {
  const automatic = getReportCurrency(viewedCountries);
  // A choice of the user until the offers viewed change their currency
  const [chosen, setChosen] = useState<null | MoneyCurrency>(null);
  useEffect(() => setChosen(null), [automatic]);
  const rate = useExchangeRate();
  const currency = chosen ?? automatic;

  return {
    convertFrom: (country, value) =>
      convertMoney(value, currencyOfCountry(country), currency, rate),
    currency,
    rate,
    setCurrency: setChosen,
    symbol: currencySymbol(currency),
  };
};
